"use client";
import dynamic from "next/dynamic";
import type { StoreData } from "@features/stores/types";
import { activeSiteConfig } from "@config/site/site-config";
import type { LineItem, Money, ShippingInfo, TaxedPrice } from "@graphql/generated/types";
import { ShoppingBag } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import CommonCMS from "@lib/constants/contentful/cms-common";
import { shoppingBagPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { mapToMoneyProps } from "@lib/utils/cart-utility/cartUtility";
import { isClickAndCollect } from "@lib/utils/productUtility";
import { useStore } from "@store/store";
import { Text } from "@ecco/ui/text";
import { Divider } from "@ecco/ui/divider";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Circle } from "@ecco/ui/circle";
import { Flex } from "@ecco/ui/flex";
import { VStack } from "@ecco/ui/stack";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { isEmpty } from "lodash-es";
import type { ReactNode, JSX } from "react";
import { useMemo, useRef } from "react";
import { GiftWrapping } from "./GiftWrapping";
import { summaryStyles, textStyles } from "./SummaryStyles";
import { DiscountPrice } from "./discount-price";
import PromoCodeRow from "./promo-code-row/PromoCodeRow";
import { SummaryRow } from "./summary-row";
import { checkClickAndCollectAvailability } from "@components/commerce/helpers";
import { CheckoutAvailabilityIndicator } from "@features/checkout/components/delivery-step/CheckoutAvailabilityIndicator";
import { getRoundedCartTotal } from "@lib/utils/moneyUtils";
import { usePathname } from "@i18n/routing";
import { ShippingMethodTypes } from "@features/checkout/types";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { useUpdateCart } from "@lib/commerce/hooks/checkoutOperations";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { buildSupplyChannelActions } from "@lib/utils/checkout-utility/supplyChannelActions";
import { hasGraphQLErrors } from "@lib/utils/graphql/hasGraphQLErrors";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { useLock } from "@store/hooks/useLock";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

const StoreLocatorModal = dynamic(
    () => import("@features/checkout/components/delivery-step/CheckoutStoreLocatorModal"),
    { ssr: false }
);

interface SummaryProps {
    locale: string;
    cartTotalPrice: Money;
    cartDiscount: Money;
    subtotalPrice: Money;
    cartTaxedPrice?: TaxedPrice;
    withPromoCodeSection?: boolean;
    shippingInfo?: ShippingInfo;
    children?: ReactNode;
    isOmnichannelMarket?: boolean;
    availabilityForClickAndCollect?: {
        lineItems?: LineItem[];
        storeData?: StoreData;
        storesData?: StoreData[];
        available?: boolean;
    };
    setCartOrSessionChanged?: (cartOrSessionChanged: boolean) => void;
    cartOrSessionChanged?: boolean;
    shouldDisplayCheckbox?: boolean;
    shouldDisplayInSummary?: boolean;
    usedDiscountCodeIds?: string[];
    isCheckout?: boolean;
    lineItems?: LineItem[];
    unavailableItemsSummary?: UnavailableItemsSummary;
}

export interface UnavailableItemsSummary {
    unavailableCount: number;
    availableCount: number;
    unavailableTotal: Money | null;
}

export const summaryContainer = "bag-summary-container";
export const headerTextId = "header-text";
export const divider = "divider";

export interface SummaryHeaders {
    subtotal: string;
    discount: string;
    shipping: string;
    salesTaxTitle: string;
    salesTaxInfo: string;
    total: string;
    free: string;
    title: string;
    eccoStore: string;
    headline: string;
    available: string;
    availableSoon: string;
    changeStore: string;
    selectStore: string;
    removeStore: string;
}

export const Summary = ({
    locale,
    shippingInfo,
    cartTotalPrice,
    cartDiscount,
    subtotalPrice,
    cartTaxedPrice,
    isOmnichannelMarket = false,
    withPromoCodeSection,
    children,
    setCartOrSessionChanged,
    cartOrSessionChanged,
    availabilityForClickAndCollect,
    shouldDisplayCheckbox,
    shouldDisplayInSummary,
    usedDiscountCodeIds,
    isCheckout = false,
    lineItems,
    unavailableItemsSummary,
}: SummaryProps): JSX.Element => {
    const { getMultiple, get: getMicrocopy } = useMicrocopy();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const storeLocatorFinalFocusRef = useRef<HTMLButtonElement>(null);
    const pathname = usePathname();
    const setStoreData = useStore((state) => state.setStoreData);
    const storeData = useStore((state) => state.data.storeData);
    const cart = useStore((state) => state.data.me.cartInfo.cart);
    const isAvailableNow =
        cart && storeData ? checkClickAndCollectAvailability(cart, storeData) : false;
    const executeUpdateCart = useUpdateCart();
    const errorToaster = useErrorToast();
    const lock = useLock();

    const microcopies = (() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.subtotal,
            CommonCMS.discount,
            CommonCMS.shipping,
            CommonCMS.salesTax.title,
            CommonCMS.salesTax.info,
            CommonCMS.total,
            CommonCMS.free,
            ShoppingBag.summaryTitle,
            ClickAndCollect.eccoStore,
            ClickAndCollect.headline,
            ClickAndCollect.available,
            ClickAndCollect.availableSoon,
            ClickAndCollect.changeStore,
            ClickAndCollect.selectStore,
            ClickAndCollect.removeStore,
            CommonCMS.error.general.title,
        ]);
    })();

    const headers: SummaryHeaders = {
        subtotal: microcopies[CommonCMS.subtotal],
        discount: microcopies[CommonCMS.discount],
        shipping: microcopies[CommonCMS.shipping],
        salesTaxTitle: microcopies[CommonCMS.salesTax.title],
        salesTaxInfo: microcopies[CommonCMS.salesTax.info],
        total: microcopies[CommonCMS.total],
        free: microcopies[CommonCMS.free],
        title: microcopies[ShoppingBag.summaryTitle],
        eccoStore: microcopies[ClickAndCollect.eccoStore],
        headline: microcopies[ClickAndCollect.headline],
        available: microcopies[ClickAndCollect.available],
        availableSoon: microcopies[ClickAndCollect.availableSoon],
        changeStore: microcopies[ClickAndCollect.changeStore],
        selectStore: microcopies[ClickAndCollect.selectStore],
        removeStore: microcopies[ClickAndCollect.removeStore],
    };

    // Removing the store must clear the supply channel and shipping method on the cart
    // itself, otherwise the cart still looks like a Click & Collect cart on checkout.
    const handleRemoveStore = lock(async () => {
        if (!cart) {
            setStoreData(undefined);
            return;
        }

        const { errors } = await executeCommerceToolsWithRetry(
            executeUpdateCart,
            {
                cartId: cart.id,
                cartVersion: cart.version,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                locale,
                actions: [
                    ...buildSupplyChannelActions(cart.lineItems, {}),
                    { setShippingMethod: {} },
                ],
            },
            0,
            "Remove store from bag summary"
        );

        if (hasGraphQLErrors(errors)) {
            Logger.error(ServiceType.WEB, "Failed to clear store from cart", {
                tag: LogTag.SHIPPING,
                cartId: cart.id,
                errors,
            });
            errorToaster({
                title: microcopies[CommonCMS.error.general.title],
            });
            return;
        }

        setStoreData(undefined);
    });

    const effectiveLineItems = lineItems || cart?.lineItems || [];
    const hasLineItems = effectiveLineItems.length > 0;
    const effectiveShippingInfo = hasLineItems ? shippingInfo : undefined;

    // CommerceTools does not provide the subtotal before shipping, so we need
    // to calculate it. If there is a shipping discount, use it.
    let shippingPrice =
        effectiveShippingInfo?.discountedPrice?.value ?? effectiveShippingInfo?.price;
    // In case we don't have shipping info, display it as 0.
    if (!shippingPrice) {
        shippingPrice = {
            centAmount: 0,
            currencyCode: cartTotalPrice?.currencyCode,
            fractionDigits: cartTotalPrice?.fractionDigits,
            type: cartTotalPrice?.type,
        };
    }

    // If the market requires rounding (e.g. Hungary: nearest 5 HUF), apply it to
    // line items, discount, and total. Same helper is used by the payment-step
    // form so the displayed total and the Adyen amount stay consistent.
    const rounded = getRoundedCartTotal({
        totalGross: cartTotalPrice,
        subtotalPrice,
        items: effectiveLineItems,
        shippingInfo: effectiveShippingInfo,
        cartDiscount,
    });
    const finalCartDiscount = !hasLineItems
        ? null
        : (rounded?.discount ?? (cartDiscount ? { ...cartDiscount } : null));
    const finalCartTotal = rounded?.total ?? { ...cartTotalPrice };

    const subTotalBeforeShipping = {
        ...subtotalPrice,
        centAmount: hasLineItems
            ? (subtotalPrice?.centAmount ?? 0) - (shippingPrice?.centAmount ?? 0)
            : 0,
    };
    const subtotalProps = mapToMoneyProps(locale, subTotalBeforeShipping);
    const discountProps = mapToMoneyProps(locale, finalCartDiscount);

    const totalProps = mapToMoneyProps(locale, {
        ...finalCartTotal,
        ...shippingPrice,
        // If there is store, means CC order. If that is the case, cartTotalPrice will be cartTotalPrice - shippingPrice.
        centAmount: !hasLineItems
            ? 0
            : !isEmpty(storeData)
              ? (finalCartTotal?.centAmount ?? 0) - shippingPrice?.centAmount
              : finalCartTotal?.centAmount,
    });

    const fallbackTaxedPrice: Money = {
        ...cartTotalPrice,
        centAmount: 0,
    };

    const estimatedSalesTaxProps = mapToMoneyProps(
        locale,
        // If tax calculation fails the totalTax might be null.
        // The fallback is to avoid showing NaN.
        !hasLineItems ? fallbackTaxedPrice : (cartTaxedPrice?.totalTax ?? fallbackTaxedPrice)
    );

    const isShoppingBagPage = Object.values(shoppingBagPage).some((path) =>
        pathname?.toString()?.includes(path)
    );

    const shippingRowProps = useMemo(() => {
        // Free shipping should display a "Free" text instead.
        if (shippingPrice.centAmount === 0) {
            return {
                toReplacement: (
                    <Text color="green.900" fontWeight="bold">
                        {headers.free}
                    </Text>
                ),
            };
        }

        return {
            moneyProps: mapToMoneyProps(locale, {
                ...shippingPrice,
                // If there is store, means CC order. If that is the case, shipping price is 0.
                centAmount: !isEmpty(storeData) ? 0 : shippingPrice?.centAmount,
            }),
            containerStyle: {
                // TODO: refactor this style to make more consistent and reusable
                ".chakra-money": {
                    mb: 0,
                },
            },
        };
    }, [shippingPrice, locale, storeData, headers.free]);

    // Both resolve to null until the Contentful keys exist — guarded so we
    // never render a stringified null next to the subtotal.
    const subtotalAvailableSuffix = unavailableItemsSummary?.unavailableCount
        ? getMicrocopy(CommonCMS.page, ShoppingBag.unavailable.subtotalAvailableSuffix, {
              replacements: { count: String(unavailableItemsSummary.availableCount) },
          })
        : null;
    const unavailableSummaryTitle = unavailableItemsSummary?.unavailableCount
        ? getMicrocopy(CommonCMS.page, ShoppingBag.unavailable.summaryLine, {
              replacements: { count: String(unavailableItemsSummary.unavailableCount) },
          })
        : null;

    return (
        <Box
            data-testid={summaryContainer}
            mt={[2, 2, 2, 0]}
            py={6}
            px={isShoppingBagPage ? 3 : [0, 4, 4, 6]}
            bg={"white"}
            pb={12}
        >
            {headers.title && (
                <Text size={"h5"} sx={summaryStyles.header} data-testid={headerTextId}>
                    {headers.title}
                </Text>
            )}
            <SummaryRow
                title={
                    subtotalAvailableSuffix
                        ? `${headers.subtotal} ${subtotalAvailableSuffix}`
                        : headers.subtotal
                }
                moneyProps={subtotalProps}
                textStyle={summaryStyles.text}
            />
            {Boolean(unavailableItemsSummary?.unavailableCount && unavailableSummaryTitle) && (
                <SummaryRow
                    title={unavailableSummaryTitle}
                    containerStyle={{ color: "red.200" }}
                    textStyle={{ color: "red.200" }}
                    toReplacement={
                        unavailableItemsSummary.unavailableTotal ? (
                            <Box color="red.200">
                                <DiscountPrice
                                    priceProps={mapToMoneyProps(
                                        locale,
                                        unavailableItemsSummary.unavailableTotal
                                    )}
                                />
                            </Box>
                        ) : (
                            <span />
                        )
                    }
                />
            )}
            {withPromoCodeSection && (
                <PromoCodeRow
                    setCartOrSessionChanged={setCartOrSessionChanged}
                    cartOrSessionChanged={cartOrSessionChanged}
                    locale={locale}
                    usedDiscountCodeIds={usedDiscountCodeIds}
                />
            )}
            {isClickAndCollect(availabilityForClickAndCollect?.storeData?.supplyChannel?.[0].id) &&
                isOmnichannelMarket &&
                isShoppingBagPage &&
                !isEmpty(storeData) && (
                    <>
                        <Divider sx={summaryStyles.divider} data-testid={divider} />
                        <VStack gap={0} alignItems={"space-between"}>
                            <SummaryRow
                                title={headers.headline}
                                textStyle={{ mb: 0 }}
                                containerStyle={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}
                                toReplacement={
                                    <Flex
                                        alignItems={["baseline", "center"]}
                                        paddingY={[2, 0]}
                                        gap={1.5}
                                    >
                                        <Circle
                                            size={2}
                                            bg={
                                                availabilityForClickAndCollect?.available
                                                    ? "green.200"
                                                    : "yellow.200"
                                            }
                                        />
                                        <Text fontWeight="bold" fontSize="desktopBodyTextNormal">
                                            {availabilityForClickAndCollect?.available
                                                ? headers.available
                                                : headers.availableSoon}
                                        </Text>
                                    </Flex>
                                }
                            />
                            <Flex justifyContent="space-between" mt={4}>
                                <Flex justifyContent="center" alignItems="center">
                                    <ECCOIcon name="finder" />
                                    <Text color="gray.500">
                                        {availabilityForClickAndCollect?.storeData?.name}
                                    </Text>
                                </Flex>
                                <Flex gap="2">
                                    <Button
                                        ref={storeLocatorFinalFocusRef}
                                        onClick={hasLineItems ? onOpen : undefined}
                                        size="sm"
                                        fontWeight="regular"
                                        variant={"link"}
                                        fontSize={["mobileLinkL1", "desktopBodyTextNormal"]}
                                        tabIndex={hasLineItems ? undefined : -1}
                                        aria-disabled={!hasLineItems || undefined}
                                        pointerEvents={hasLineItems ? undefined : "none"}
                                    >
                                        <span>{headers.changeStore}</span>
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            void handleRemoveStore().catch((error) => {
                                                Logger.error(
                                                    ServiceType.WEB,
                                                    "Failed to clear store from cart",
                                                    {
                                                        tag: LogTag.SHIPPING,
                                                        cartId: cart?.id,
                                                        errors: error,
                                                    }
                                                );
                                                errorToaster({
                                                    title: microcopies[
                                                        CommonCMS.error.general.title
                                                    ],
                                                });
                                            });
                                        }}
                                        size="sm"
                                        fontWeight="regular"
                                        variant={"link"}
                                        fontSize={["mobileLinkL1", "desktopBodyTextNormal"]}
                                    >
                                        <span>{headers.removeStore}</span>
                                    </Button>
                                </Flex>
                            </Flex>
                        </VStack>
                        {isOpen && (
                            <StoreLocatorModal
                                isOpen={isOpen}
                                onClose={onClose}
                                selectedProductData={availabilityForClickAndCollect?.lineItems}
                                storesWithStock={availabilityForClickAndCollect?.storesData}
                                location="cartSummary"
                                shippingMethodKey={ShippingMethodTypes.CLICK_AND_COLLECT}
                                finalFocusRef={storeLocatorFinalFocusRef}
                            />
                        )}
                    </>
                )}
            <Divider sx={summaryStyles.divider} data-testid={divider} />
            <VStack gap={4} alignItems={"space-between"}>
                {discountProps && (
                    <SummaryRow
                        title={headers.discount}
                        textStyle={{ ...textStyles, mb: 0 }}
                        containerStyle={{
                            alignItems: "center",
                        }}
                        toReplacement={<DiscountPrice priceProps={discountProps} />}
                    />
                )}
                {activeSiteConfig.isGiftWrappingAllowed && (
                    <GiftWrapping
                        shouldDisplayCheckbox={shouldDisplayCheckbox}
                        shouldDisplayInSummary={shouldDisplayInSummary}
                    />
                )}
                {!isEmpty(storeData) && isCheckout ? (
                    <Flex flexDirection={"column"}>
                        <SummaryRow
                            title={headers.headline}
                            textStyle={summaryStyles.text}
                            {...shippingRowProps}
                        />
                        <CheckoutAvailabilityIndicator isAvailableNow={isAvailableNow} />
                    </Flex>
                ) : (
                    <SummaryRow
                        title={headers.shipping}
                        textStyle={summaryStyles.text}
                        {...shippingRowProps}
                    />
                )}
                {activeSiteConfig.checkout.requiresSalesTaxEstimation && (
                    <SummaryRow
                        title={headers.salesTaxTitle}
                        moneyProps={estimatedSalesTaxProps}
                        textStyle={summaryStyles.text}
                        containerStyle={{
                            // TODO: refactor this style to make more consistent and reusable
                            ".chakra-money:last-of-type": {
                                mb: 0,
                            },
                        }}
                        tooltipInfo={
                            <Box>
                                <Text sx={summaryStyles.tooltipHeader}>
                                    {headers.salesTaxTitle}
                                </Text>
                                <Text sx={summaryStyles.tooltipText}>{headers.salesTaxInfo}</Text>
                            </Box>
                        }
                    />
                )}
            </VStack>
            <Divider sx={summaryStyles.divider} data-testid={divider} />
            <SummaryRow
                title={headers.total}
                moneyProps={totalProps}
                containerStyle={{
                    ...textStyles,
                    span: {
                        mb: "0",
                    },
                }}
                textStyle={textStyles}
            />
            {children}
        </Box>
    );
};

export default Summary;
